import React, { useEffect } from 'react';
import { DocumentNode, useLazyQuery } from '@apollo/client';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { DetailsListLayoutMode, IColumn, IPivotProps, SelectionMode } from '@fluentui/react';
import FullWidthHeader from '../../common/headers/FullWidthHeader';
import {
  bodyContentContainer,
  styledDetailsList,
} from '../../../app/common/styles/CommonStyleObjects';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import ShimmeredDetailsListWrapper, {
  GetUpdatedColumnState,
} from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import DetailListPaginationBanner from '../../common/DetailsLists/pagination/DetailsListPaginationBanner';
import { IDetailsListColumnSortProps } from '../../../utils/types/IDetailsListColumnSortProps';
import useUrlPagination from '../../../utils/hooks/useUrlPagination';
import IPaginationMetadata from '../../common/DetailsLists/pagination/IPaginationMetadata';
import useUrlSorting, { getSortObject } from '../../../utils/hooks/useUrlSorting';

/**
 * Default system wide max records per paginated page
 */
export const DEFAULT_RECORDS_PER_PAGE = 15;

export function getInitialPageSize(defaultPageSize?: number): number {
  return defaultPageSize != null && defaultPageSize !== 0
    ? defaultPageSize
    : DEFAULT_RECORDS_PER_PAGE;
}

export function getTotalPageCountImproved(
  totalRecordCount: number,
  defaultPageSize: number,
): number {
  // check for 0
  if (defaultPageSize <= 0) return 1;
  return Math.ceil(totalRecordCount / defaultPageSize);
}

export interface IPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  endCursor: string;
  startCursor: string;
}

export function getPaginationInfo(
  pageInfo: IPageInfo,
  selectedPage: number,
  newPageNumber: number,
  defaultPageSize: number,
): { first: number | null; after: string | null; last: number | null; before: string | null } {
  const { hasNextPage, hasPreviousPage, endCursor, startCursor } = pageInfo;
  const goingBackward = newPageNumber < selectedPage && hasPreviousPage;
  const goingForward = newPageNumber > selectedPage && hasNextPage;

  if (goingForward) {
    return {
      first: defaultPageSize,
      after: endCursor,
      last: null,
      before: null,
    };
  }

  if (goingBackward) {
    return {
      first: null,
      after: null,
      last: defaultPageSize,
      before: startCursor,
    };
  }

  // Fallback (e.g. navigating to page 1 or any case neither flag applies)
  return {
    first: defaultPageSize,
    after: null,
    last: null,
    before: null,
  };
}

interface IPaginationProps<TData = unknown> {
  query: DocumentNode;
  columns: IColumn[];
  label: string;
  queryResultKey: string;
  defaultSortColumn: string;
  defaultPageSize?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchDto?: any;
  children?: JSX.Element;
  onCompletedQueryUpdate?: (data: TData) => void;
  pivotViewNavigation?: React.FunctionComponent<IPivotProps>;
  noResultsMessageBar?: (data: TData, loading: boolean) => JSX.Element;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemFilter?: (item: any) => boolean;
}

type CursorVars = {
  first: number | null;
  after: string | null;
  last: number | null;
  before: string | null;
};

const Pagination = (props: IPaginationProps) => {
  const {
    query,
    columns,
    label,
    queryResultKey,
    defaultSortColumn,
    defaultPageSize,
    searchDto,
    children,
    onCompletedQueryUpdate,
    pivotViewNavigation,
    noResultsMessageBar,
    itemFilter,
  } = props;

  const [paginationState, setPaginationState] = useUrlPagination({
    pageSize: getInitialPageSize(defaultPageSize),
  });
  const [sortedState, setSortedState] = useUrlSorting(defaultSortColumn);
  const firstLoad = React.useRef(true);

  const sortedColumns = GetUpdatedColumnState(
    sortedState.sortedFieldName,
    columns,
    (sortedState.sortOrder === 'DESC').toString(),
  );

  // Helper to normalize mutually exclusive forward/backward vars
  const buildCursorVars = (opts: Partial<CursorVars>): CursorVars => {
    const { first = null, after = null, last = null, before = null } = opts;

    if (first !== null) {
      return { first, after, last: null, before: null };
    }
    if (last !== null) {
      return { first: null, after: null, last, before };
    }
    return { first: null, after: null, last: null, before: null };
  };

  const [refetch, { data, loading, error }] = useLazyQuery(query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      ...buildCursorVars({ first: paginationState.pageSize, after: null }),
      order: sortedState.sortObject,
      searchDto,
    },
    onCompleted: (completedData) => {
      if (onCompletedQueryUpdate) {
        onCompletedQueryUpdate(completedData);
      }
    },
  });

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
    } else {
      setPaginationState({ ...paginationState, currentPage: 1 });
    }

    refetch({
      variables: {
        ...buildCursorVars({ first: paginationState.pageSize, after: null }),
        order: sortedState.sortObject,
        searchDto,
      },
    });
  }, [searchDto]);

  // Reset pagination state to previous page
  useEffect(() => {
    if (
      paginationState.currentPage !== 1 &&
      data &&
      data[queryResultKey]?.pageInfo &&
      !data[queryResultKey]?.pageInfo.hasPreviousPage
    ) {
      setPaginationState((prev) => ({ ...prev, currentPage: 1 }));
    }
  }, [data, queryResultKey]);

  const onPageSizeChange = (newPageSize: string | number): void => {
    const parsedPageSize =
      typeof newPageSize === 'string' ? parseInt(newPageSize, 10) : newPageSize;

    // Validate page size
    if (Number.isNaN(parsedPageSize) || parsedPageSize <= 0) {
      return;
    }

    const paginationData = {
      currentPage: 1,
      pageSize: parsedPageSize,
      skip: 0, // Keep for compatibility, not used in cursor pagination
    };

    setPaginationState(paginationData);

    refetch({
      variables: {
        ...buildCursorVars({ first: paginationData.pageSize, after: null }),
        order: sortedState.sortObject,
        searchDto,
      },
    });
  };

  const onPageChange = (
    startItemIndex: number,
    endItemIndex: number,
    newPageNumber: number,
  ): void => {
    const paginationData = structuredClone(paginationState);
    paginationData.currentPage = newPageNumber;
    paginationData.skip = 0; // Keep for compatibility, not used in cursor pagination

    // Enhanced cursor-based pagination logic
    const currentPageInfo = data ? data[queryResultKey]?.pageInfo : null;

    if (currentPageInfo) {
      const paginationInfo = getPaginationInfo(
        currentPageInfo,
        paginationState.currentPage,
        newPageNumber,
        paginationData.pageSize,
      );

      refetch({
        variables: {
          ...buildCursorVars({
            first: paginationInfo.first,
            after: paginationInfo.after,
            last: paginationInfo.last,
            before: paginationInfo.before,
          }),
          order: sortedState.sortObject,
          searchDto,
        },
      });
    } else {
      // Fallback to simple forward pagination for initial load
      const isGoingForward = newPageNumber > paginationState.currentPage;

      refetch({
        variables: {
          ...buildCursorVars({
            first: paginationData.pageSize,
            after: isGoingForward && currentPageInfo?.endCursor ? currentPageInfo.endCursor : null,
          }),
          order: sortedState.sortObject,
          searchDto,
        },
      });
    }

    setPaginationState(paginationData);
  };

  const totalRecordCount: number = data ? data[queryResultKey]?.totalCount : 0;

  const paginationMetadata: IPaginationMetadata = {
    loadingData: loading,
    pageCount:
      getTotalPageCountImproved(totalRecordCount, paginationState.pageSize) === 0
        ? 1
        : getTotalPageCountImproved(totalRecordCount, paginationState.pageSize),
    selectedPage: paginationState.currentPage,
    pageSize: paginationState.pageSize,
    onPageChange,
    onPageSizeChange,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateStateResetPagination = (newState: any) => {
    const paginationData = structuredClone(paginationState);
    paginationData.currentPage = 1;
    paginationData.skip = 0;

    const newSortedState = structuredClone(sortedState);

    const order = getSortObject(
      newState.sortedFieldName,
      newState.isSortedDescending === 'true' ? 'DESC' : 'ASC',
    );

    newSortedState.sortedFieldName = newState.sortedFieldName;
    newSortedState.sortObject = order;
    newSortedState.sortOrder = newState.isSortedDescending === 'true' ? 'DESC' : 'ASC';

    refetch({
      variables: {
        ...buildCursorVars({ first: paginationData.pageSize, after: null }),
        order,
        searchDto,
      },
    });

    setPaginationState(paginationData);
    setSortedState(newSortedState);
  };

  // These event handlers are not used, but instead one state update for the sorting and pagination
  // are done in the above function.  This is to prevent multiple renders.
  const translateSortOrder = (isSortedDescending: boolean) => {};
  const setSortedFieldName = (sortedFieldName: string) => {};
  const setSortedColumns = (newColumns: IColumn[]) => {};

  const detailsListColumnSortProps: IDetailsListColumnSortProps = {
    columns: sortedColumns,
    sortedFieldName: sortedState.sortedFieldName,
    searchState: searchDto,
    isSortedDescending: sortedState.sortOrder === 'DESC',
    setColumns: setSortedColumns,
    setSortedFieldName,
    setIsSortedDescending: translateSortOrder,
    updateStateResetPagination,
  };
  const rawItems =
    data && data[queryResultKey]?.edges
      ? data[queryResultKey].edges.map((edge: { node: unknown }) => edge.node)
      : [];

  const items = itemFilter ? rawItems.filter(itemFilter) : rawItems;

  return (
    <>
      <FullWidthHeader
        title={() => <h1>{label}</h1>}
        subtitle={() => {
          if (pivotViewNavigation) {
            return (
              <Stack>
                {children && <StackItem>{children}</StackItem>}
                <StackItem>{pivotViewNavigation({})}</StackItem>
              </Stack>
            );
          }

          return <>{children}</>;
        }}
      />
      <div className={`${bodyContentContainer}  ms-depth-4`}>
        <LoadingErrorMessage error={error} loading={loading} />
        {noResultsMessageBar && noResultsMessageBar(data, loading)}
        <ShimmeredDetailsListWrapper
          items={items?.length ? items : []}
          columns={columns}
          layoutMode={DetailsListLayoutMode.justified}
          enableShimmer={loading}
          selectionMode={SelectionMode.none}
          detailsListStyles={styledDetailsList}
          ariaLabelForShimmer={`${label} are being fetched`}
          ariaLabelForGrid={`${label} details`}
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          pluralListLabel={label}
          singularListLabel={label}
          isColumnSortedList
          detailsListColumnSortProps={detailsListColumnSortProps}
        />
        {paginationMetadata && <DetailListPaginationBanner {...paginationMetadata} />}
      </div>
    </>
  );
};

export default Pagination;
